import { FaArrowDown } from "react-icons/fa6";
import scrollToMessage from "../../../utils/scrollToMessage";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import {
  removeMessageList,
  replaceMessageWindow,
} from "../../../store/Slices/MessageListSlice";
import {
  resetMissedBelow,
  updateMessageOptions,
} from "../../../store/Slices/MessageOptionsSlice";
import { fetchChatMessages } from "../../../utils/fetchChatMessages";
import { getNewestMessageId } from "../../../utils/messageListWindow";
import { messageListPayloadChatId } from "../../../utils/socketQueries";

/**
 * ↓ FAB + badge. Badge count comes ONLY from live socket `receive`
 * (MessageOptions.missed_below_count) — never from tip/history list diffs
 * (those caused false +13/+17 on huge old bubbles like #12933).
 */
export default function OnClickScrollToBottom() {
  const [atBottom, setAtBottom] = useState(true);
  const MessageList = useAppSelector((state) => state.MessageList);
  const messageOptions = useAppSelector((state) => state.MessageOptions);
  const currentConversation = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const prevChatRef = useRef(currentConversation.chat_id);
  const activeChatRef = useRef(currentConversation.chat_id);
  activeChatRef.current = currentConversation.chat_id;

  const missedCount = messageOptions.missed_below_count || 0;

  // Sync at-bottom into Redux for ListenAllEvents (live badge / no auto-scroll)
  useEffect(() => {
    dispatch(
      updateMessageOptions({
        message_list_at_bottom: atBottom,
      }),
    );
    if (atBottom) dispatch(resetMissedBelow());
  }, [atBottom, dispatch]);

  // Reset when switching chats
  useEffect(() => {
    if (prevChatRef.current !== currentConversation.chat_id) {
      prevChatRef.current = currentConversation.chat_id;
      setAtBottom(true);
      dispatch(resetMissedBelow());
      dispatch(updateMessageOptions({ message_list_at_bottom: true }));
    }
  }, [currentConversation.chat_id, dispatch]);

  useEffect(() => {
    if (!MessageList?.message_list?.Records?.length) return;

    const lastId = String(
      MessageList.message_list.Records[
        MessageList.message_list.Records.length - 1
      ].message_id,
    );

    const lastElement = document.getElementById(lastId);
    if (!lastElement) return;

    const root = document.getElementById("messageListDiv");
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting ?? true;
        setAtBottom(visible);
      },
      {
        root: root || null,
        threshold: 0.15,
        rootMargin: "0px 0px -72px 0px",
      },
    );

    observer.observe(lastElement);
    return () => observer.disconnect();
  }, [MessageList.message_list.Records]);

  async function goToBottom() {
    dispatch(resetMissedBelow());

    if (messageOptions.has_more_newer) {
      dispatch(updateMessageOptions({ isMessageLoading: true }));
      try {
        queryClient.removeQueries({
          queryKey: ["message_list", currentConversation.chat_id],
        });
        const chatId = currentConversation.chat_id;
        const res = await fetchChatMessages({
          chat_id: chatId,
          user_id: currentConversation.user_id,
          page: 1,
          pageSize: Math.max(50, currentConversation.unseen_count || 0),
          pinned: true,
        });
        if (Number(activeChatRef.current) !== Number(chatId)) return;
        const payloadChatId = messageListPayloadChatId(res);
        if (payloadChatId != null && Number(payloadChatId) !== Number(chatId)) {
          return;
        }
        dispatch(removeMessageList());
        dispatch(replaceMessageWindow(res));
        const pagination = res.message_list?.Pagination;
        dispatch(
          updateMessageOptions({
            historyMode: "page",
            has_more_older:
              pagination?.has_more_older ??
              Number(pagination?.current_page) <
                Number(pagination?.total_pages),
            has_more_newer: false,
            isMessageLoading: false,
            currentPage: "1",
            totalPages: Number(pagination?.total_pages || 0),
            message_list_at_bottom: true,
          }),
        );
        const tipId =
          res.message_list?.Records?.[0]?.message_id ||
          getNewestMessageId(res.message_list?.Records || []);
        if (tipId) {
          setTimeout(() => scrollToMessage(tipId, false, true), 80);
        }
        setAtBottom(true);
      } catch {
        dispatch(updateMessageOptions({ isMessageLoading: false }));
      }
      return;
    }

    const last =
      MessageList.message_list.Records[
        MessageList.message_list.Records.length - 1
      ];
    if (last) {
      scrollToMessage(last.message_id, false, true);
      setAtBottom(true);
    }
  }

  const show = !atBottom || missedCount > 0;
  const countLabel =
    missedCount > 99 ? "99+" : missedCount > 0 ? String(missedCount) : "";

  return (
    <div
      className={`pointer-events-none absolute bottom-[4.6rem] right-[calc(5%+0.4rem)] z-40 flex flex-col items-center gap-1.5 transition-all duration-300 ease-out sm:bottom-[4.85rem] ${
        show
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-12 opacity-0"
      }`}
      aria-hidden={!show}
    >
      {countLabel ? (
        <span className="grid min-h-7 min-w-7 place-content-center rounded-full bg-themeColor px-1.5 text-[11px] font-semibold tabular-nums text-themeTextColor shadow-sm">
          {countLabel}
        </span>
      ) : null}

      <button
        type="button"
        onClick={goToBottom}
        disabled={!show}
        tabIndex={show ? 0 : -1}
        className={`grid h-9 w-9 place-content-center rounded-full bg-black/45 text-white shadow-sm backdrop-blur-md transition active:scale-95 ${
          show
            ? "pointer-events-auto cursor-pointer"
            : "pointer-events-none cursor-default"
        }`}
        aria-label="Scroll to bottom"
      >
        <FaArrowDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
